import { z } from 'zod';

import { ActorSchema, TaskStatusSchema } from '../task/task';

import {
  CardCompanySnapshotSchema,
  CardCustomerSnapshotSchema,
  CardOriginChannelSchema,
} from './card';
import { CardIdSchema } from './card_id';

/**
 * Per-card task rollup shown on the conversation board card footer
 * (progress pips + done/total). `statuses` keeps task order so the
 * board can render one tone-coded pip per task.
 */
export const CardTaskProgressSchema = z.object({
  total: z.number().int().min(0),
  done: z.number().int().min(0),
  statuses: z.array(TaskStatusSchema).max(50),
});

export type CardTaskProgress = z.infer<typeof CardTaskProgressSchema>;

/**
 * Slim parent-card projection rendered by a conversation-board card —
 * only the fields the board tile shows. Mirrors the task board's
 * {@link TaskCardCellSchema} so neither board ships the full persisted
 * `Card` (chat, participants, externalRefs, closure, …) to the client;
 * the drawer fetches the full `CardDetail` on demand.
 */
export const ConversationBoardCardCellSchema = z.object({
  id: CardIdSchema,
  seq: z.number().int().min(1),
  subject: z.string().max(200).nullable(),
  originChannel: CardOriginChannelSchema,
  customer: CardCustomerSnapshotSchema,
  company: CardCompanySnapshotSchema,
  assigneeAgent: ActorSchema.nullable(),
});

export type ConversationBoardCardCell = z.infer<typeof ConversationBoardCardCellSchema>;

/** A conversation-board card: the slim card cell plus its task rollup
 * and the resolved display name of the current assignee (null when
 * unassigned). */
export const ConversationBoardCardSchema = ConversationBoardCardCellSchema.extend({
  // Tolerant defaults so a response without the rollup still parses (the
  // server always sends them; this keeps the board resilient).
  taskProgress: CardTaskProgressSchema.default({ total: 0, done: 0, statuses: [] }),
  agentDisplayName: z.string().nullable().default(null),
});

export type ConversationBoardCard = z.infer<typeof ConversationBoardCardSchema>;
